import { Component, OnInit, Input, EventEmitter, Output, OnChanges, SimpleChanges, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FormGroup,
  Validators,
  FormControl,
  ControlContainer,
  FormGroupDirective,
  AbstractControl,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PhoneBookVisibility, Subscriber } from '@shared/models/subscriber.model';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { getCurrentAge, maxAgeValidator, minAgeValidator } from '@sales/validators/age.validator';
import { ErrorStateOnInvalid } from '@app/material/ErrorStateOnInvalid';
import { IconNames } from '@shared/enums/icon-names';
import { VoiceSubscriptionType } from '@sales/modules/subscription/models/offerings-response.model';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { MatError } from '@angular/material/form-field';
import { MatRadioGroup } from '@angular/material/radio';
import { IceDatePickerComponent } from '@app/shared/components/molecules/ice-date-picker/ice-date-picker.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { noIllegalCharactersValidator } from '@app/sales/validators/no-illegal-characters-validator';
import { FullNameValidator } from '@app/sales/validators/full-name-validator';
import { capitalizeString } from '@app/core/utils';
import { Regex } from '@app/shared/enums/regex';

@Component({
  selector: 'app-user-definer-step',
  templateUrl: './user-definer-step.component.html',
  styleUrls: ['./user-definer-step.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceAlertComponent,
    IceInputFieldComponent,
    MatCheckbox,
    IceDatePickerComponent,
    MatRadioGroup,
    MatError,
    IceSelectBoxComponent,
  ],
})
export class UserDefinerStepComponent implements OnInit, OnChanges, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);

  IconNames = IconNames;
  PhoneBookVisibility = PhoneBookVisibility;
  getEmailErrorText = FormValidationService.getEmailErrorText;
  getNameErrorText = FormValidationService.getNameErrorText;

  @Input() subscriber: Subscriber;
  @Input() productId: string;
  @Input() upperAgeLimit: number;
  @Input() lowerAgeLimit: number;
  @Input() voiceSubscriptionType: VoiceSubscriptionType;
  @Input() showUser: boolean = true;
  @Input() groupName: string;
  @Input() hasInteractedWithStep: boolean;
  @Output() isInvalid = new EventEmitter();
  @Output() updateSubscriber = new EventEmitter();

  form: FormGroup;
  maxDate: Date;
  minDate: Date;

  subscriptions$: Subscription[] = [];
  matcher = new ErrorStateOnTouched();
  invalidMatcher = new ErrorStateOnInvalid();

  get isHiddenFromPhoneBookRequired() {
    return this.upperAgeLimit && this.upperAgeLimit < 18;
  }

  get upperAgeLimitErrorText() {
    return this.upperAgeLimit ? `Brukeren må være under ${this.upperAgeLimit} år` : '';
  }
  get lowerAgeLimitErrorText() {
    return this.lowerAgeLimit ? `Brukeren må være minst ${this.lowerAgeLimit} år` : '';
  }
  get ageLimitErrorText() {
    return this.upperAgeLimitErrorText || this.lowerAgeLimitErrorText;
  }

  private static isBirthdayDateValid = () => {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const minDate = new Date('1900-01-01');
      const maxDate = new Date(new Date().setDate(new Date().getDate() - 1));
      const newDate = new Date(control.value);
      return newDate >= minDate && newDate <= maxDate ? null : { invalidBirthdayDate: true };
    };
  };

  static buildUserDefinerFormGroup(
    name: string,
    birthday: Date,
    address: string,
    postCode: string,
    city: string,
    email: string,
    dontHaveEmail: boolean,
    phoneBookVisibility: PhoneBookVisibility,
    upperAgeLimit: number,
    lowerAgeLimit: number
  ): FormGroup {
    const hasUpperAgeValidator = upperAgeLimit ? maxAgeValidator(upperAgeLimit) : () => null;
    const hasLowerAgeValidator = lowerAgeLimit ? minAgeValidator(lowerAgeLimit) : () => null;

    return new FormGroup({
      name: new FormControl(name, [Validators.required, noIllegalCharactersValidator(), FullNameValidator()]),
      birthday: new FormControl(birthday, [
        Validators.required,
        hasUpperAgeValidator,
        hasLowerAgeValidator,
        UserDefinerStepComponent.isBirthdayDateValid(),
      ]),
      address: new FormControl(address, [Validators.required]),
      postCode: new FormControl(postCode, [Validators.required]),
      city: new FormControl(city, [Validators.required]),
      email: new FormControl(dontHaveEmail ? '' : email, [
        Validators.required,
        Validators.pattern(Regex.Email),
        Validators.maxLength(50),
      ]),
      dontHaveEmail: new FormControl(dontHaveEmail, [Validators.required]),
      phoneBookVisibility: new FormControl(phoneBookVisibility, [Validators.required]),
    });
  }

  ngOnInit() {
    this.maxDate = new Date(new Date().setDate(new Date().getDate() - 1));
    this.minDate = new Date('1900-01-01');
    this.form = this.buildForm(this.subscriber);

    if (this.subscriber.email) {
      this.form.controls.email.markAsTouched();
    }
    if (this.subscriber.birthday) {
      this.form.controls.birthday.markAsTouched();
    }

    this.subscriptions$.push(
      this.form.valueChanges.subscribe(() => {
        this.isInvalid.emit(this.form.invalid);
      })
    );
    this.isInvalid.emit(this.form.invalid);
    this.setBirthdayValidator();

    this.subscriptions$.push(
      this.form.get('dontHaveEmail').valueChanges.subscribe(val => {
        const emailControl = this.form.get('email');
        val ? emailControl.disable() : emailControl.enable();
      })
    );
    this.subscriptions$.push(
      this.form.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(formValue => {
        this.updateSubscriber.emit({ productId: this.productId, subscriber: formValue });
      })
    );
    this.subscriptions$.push(
      this.form.controls.birthday.valueChanges.subscribe(() => {
        this.autofillPhoneBookVisibility();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.upperAgeLimit || changes.lowerAgeLimit) {
      this.setBirthdayValidator();
    }

    if (changes.voiceSubscriptionType && this.isHiddenFromPhoneBookRequired) {
      this.form?.controls.phoneBookVisibility.setValue(PhoneBookVisibility.Hidden);
    }
  }

  autofillPhoneBookVisibility(): void {
    if (
      this.form.controls.phoneBookVisibility.pristine &&
      this.form.controls.birthday.valid &&
      getCurrentAge(this.form.controls.birthday.value) < 18
    ) {
      this.form.controls.phoneBookVisibility.setValue(PhoneBookVisibility.Hidden);
      this.form.controls.phoneBookVisibility.markAsDirty();
    }
  }

  capitalizeFullName(): void {
    const nameControl = this.form.controls.name;
    const capitalizedName = capitalizeString(nameControl.value, [' ', '-']);
    nameControl.setValue(capitalizedName);
  }

  private setBirthdayValidator() {
    const birthdayElement = this.form?.get('birthday');
    if (birthdayElement) {
      const validators = [Validators.required, UserDefinerStepComponent.isBirthdayDateValid()];
      if (this.upperAgeLimit) {
        validators.push(maxAgeValidator(this.upperAgeLimit));
      }
      if (this.lowerAgeLimit) {
        validators.push(minAgeValidator(this.lowerAgeLimit));
      }
      birthdayElement.setValidators(validators);
      birthdayElement.updateValueAndValidity();
    }
  }

  private buildForm(subscriber: Subscriber): FormGroup {
    if (this.formGroupDirective.form.get(this.groupName)) {
      this.formGroupDirective.form.removeControl(this.groupName);
    }
    this.formGroupDirective.form.addControl(
      this.groupName,
      UserDefinerStepComponent.buildUserDefinerFormGroup(
        subscriber?.name,
        subscriber?.birthday,
        subscriber?.address,
        subscriber?.postCode,
        subscriber?.city,
        subscriber?.email,
        !!subscriber?.dontHaveEmail,
        this.isHiddenFromPhoneBookRequired ? PhoneBookVisibility.Hidden : subscriber?.phoneBookVisibility,
        null,
        null
      )
    );

    return this.formGroupDirective.form.get(this.groupName) as FormGroup;
  }

  ngOnDestroy() {
    this.updateSubscriber.emit({ productId: this.productId, subscriber: this.form.value });
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
